// BoardTable.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import Papa from "papaparse";
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  CircularProgress,
  TablePagination,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

/* =========================
   Global Trello API Constants
   Use environment variables if defined; otherwise fallback to default values.
========================= */
const TRELLO_API_KEY =
  process.env.REACT_APP_TRELLO_KEY || "18d50606d419c001df43c639bef31408";
const TRELLO_API_TOKEN =
  process.env.REACT_APP_TRELLO_TOKEN ||
  "f847d5371155e21120eb8c5f1ef5b57a22fa331789125143db766cbd147290b3";

/* =========================
   UPLOAD SHEET SECTION
   (Drag-and-drop CSV upload with table pagination, column filters,
    enhanced Template Builder UI with edit/delete tokens and card counter,
    and bulk action that loads actual boards and members)
========================= */
const UploadSheetSection = () => {
  // File Upload & Data State
  const [sheetData, setSheetData] = useState([]);
  const [availableColumns, setAvailableColumns] = useState([]);

  // Row selection state
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  // For “select visible” feature
  const [selectVisible, setSelectVisible] = useState(false);

  // Template Builder States for Title & Description tokens
  // Each token is an object: { type: "column" | "static", value: string }
  const [titleTokens, setTitleTokens] = useState([]);
  const [descTokens, setDescTokens] = useState([]);
  // Temporary states for adding new tokens
  const [selectedColumnForTitle, setSelectedColumnForTitle] = useState("");
  const [newTitleStatic, setNewTitleStatic] = useState("");
  const [selectedColumnForDesc, setSelectedColumnForDesc] = useState("");
  const [newDescStatic, setNewDescStatic] = useState("");

  // Separator selection for concatenation
  const separatorOptions = [
    { label: "Space", value: " " },
    { label: "New Line", value: "\n" },
    { label: "Hyphen", value: "-" },
    { label: "Underscore", value: "_" },
  ];
  const [titleSeparator, setTitleSeparator] = useState(" ");
  const [descSeparator, setDescSeparator] = useState("\n");

  // New states for editable preview text for the template builder
  const [titleTemplateText, setTitleTemplateText] = useState("");
  const [descTemplateText, setDescTemplateText] = useState("");

  // Bulk Action (Destination) States
  const [selectedBulkBoard, setSelectedBulkBoard] = useState("");
  const [selectedBulkMember, setSelectedBulkMember] = useState("");

  // These states will hold the list of boards and members (loaded from Trello API)
  const [bulkBoards, setBulkBoards] = useState([]);
  const [bulkMembers, setBulkMembers] = useState([]);

  // Pagination States for the Upload Sheet table
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Column filters (one per available column)
  const [columnFilters, setColumnFilters] = useState({});

  // Drag-and-Drop state
  const [isDragging, setIsDragging] = useState(false);

  // Snackbar state for success/error messages
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // "error", "warning", "info"
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // ---------------------------
  // File Upload & Parsing
  // ---------------------------
  const processFile = (file) => {
    if (file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const data = results.data;
          // Generate an id for each row if not provided
          const dataWithId = data.map((row, index) => ({
            id: index + 1,
            ...row,
          }));
          setSheetData(dataWithId);
          // Use header names from the CSV (keys of the first row)
          if (dataWithId.length > 0) {
            const cols = Object.keys(dataWithId[0]).filter((key) => key !== "id");
            setAvailableColumns(cols);
            // Initialize column filters with "all" for each column
            const initialFilters = {};
            cols.forEach((col) => (initialFilters[col] = "all"));
            setColumnFilters(initialFilters);
          }
          // Reset tokens and row selection on new file upload
          setTitleTokens([]);
          setDescTokens([]);
          setSelectedRowIds([]);
          setSelectVisible(false);
          setPage(0);
          showSnackbar("CSV file successfully loaded.", "success");
        },
        error: (error) => {
          console.error("Error parsing file:", error);
          showSnackbar(
            "Failed to parse the file. Please check the file format.",
            "error"
          );
        },
      });
    }
  };

  // Handler for file input button
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    processFile(file);
  };

  // ---------------------------
  // Drag-and-Drop Handlers
  // ---------------------------
  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    processFile(file);
  };

  // ---------------------------
  // Column Filter Handler
  // ---------------------------
  const handleColumnFilterChange = (col, value) => {
    setColumnFilters((prev) => ({ ...prev, [col]: value }));
    setPage(0);
  };

  // Filter sheetData based on column filters
  const filteredSheetData = useMemo(() => {
    return sheetData.filter((row) =>
      availableColumns.every((col) => {
        if (!columnFilters[col] || columnFilters[col] === "all") return true;
        return row[col] === columnFilters[col];
      })
    );
  }, [sheetData, availableColumns, columnFilters]);

  // ---------------------------
  // Pagination: Compute paginated data
  // ---------------------------
  const paginatedSheetData = useMemo(() => {
    return filteredSheetData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredSheetData, page, rowsPerPage]);

  // ---------------------------
  // Row Selection Handler & "Select Visible"
  // ---------------------------
  const handleRowCheckbox = (id) => {
    setSelectedRowIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Toggle "select all visible" (paginated & filtered) rows
  const handleSelectVisible = (event) => {
    const checked = event.target.checked;
    setSelectVisible(checked);
    const visibleIds = paginatedSheetData.map((row) => row.id);
    if (checked) {
      // Add any visible rows not already selected
      setSelectedRowIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    } else {
      // Remove visible rows from selected list
      setSelectedRowIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    }
  };

  // ---------------------------
  // Template Builder Utility Functions
  // ---------------------------
  const moveToken = (tokens, fromIndex, toIndex) => {
    const newTokens = [...tokens];
    const [removed] = newTokens.splice(fromIndex, 1);
    newTokens.splice(toIndex, 0, removed);
    return newTokens;
  };

  // Title tokens reordering
  const moveUpTitleToken = (index) => {
    if (index === 0) return;
    setTitleTokens((prev) => moveToken(prev, index, index - 1));
  };
  const moveDownTitleToken = (index) => {
    if (index === titleTokens.length - 1) return;
    setTitleTokens((prev) => moveToken(prev, index, index + 1));
  };

  // Description tokens reordering
  const moveUpDescToken = (index) => {
    if (index === 0) return;
    setDescTokens((prev) => moveToken(prev, index, index - 1));
  };
  const moveDownDescToken = (index) => {
    if (index === descTokens.length - 1) return;
    setDescTokens((prev) => moveToken(prev, index, index + 1));
  };

  // ---------------------------
  // Handlers for Adding Tokens (Template Builder)
  // ---------------------------
  const addColumnToTitle = () => {
    if (selectedColumnForTitle) {
      setTitleTokens((prev) => [
        ...prev,
        { type: "column", value: selectedColumnForTitle },
      ]);
      setSelectedColumnForTitle("");
    }
  };
  const addStaticToTitle = () => {
    if (newTitleStatic.trim() !== "") {
      setTitleTokens((prev) => [
        ...prev,
        { type: "static", value: newTitleStatic.trim() },
      ]);
      setNewTitleStatic("");
    }
  };
  const addColumnToDesc = () => {
    if (selectedColumnForDesc) {
      setDescTokens((prev) => [
        ...prev,
        { type: "column", value: selectedColumnForDesc },
      ]);
      setSelectedColumnForDesc("");
    }
  };
  const addStaticToDesc = () => {
    if (newDescStatic.trim() !== "") {
      setDescTokens((prev) => [
        ...prev,
        { type: "static", value: newDescStatic.trim() },
      ]);
      setNewDescStatic("");
    }
  };

  // Edit token – for simplicity, “editing” moves the token text back to the input.
  const handleEditToken = (token, index, isTitle = true) => {
    if (isTitle) {
      if (token.type === "column") {
        setSelectedColumnForTitle(token.value);
      } else {
        setNewTitleStatic(token.value);
      }
      setTitleTokens((prev) => prev.filter((_, i) => i !== index));
    } else {
      if (token.type === "column") {
        setSelectedColumnForDesc(token.value);
      } else {
        setNewDescStatic(token.value);
      }
      setDescTokens((prev) => prev.filter((_, i) => i !== index));
    }
  };

  // Delete token
  const handleDeleteToken = (index, isTitle = true) => {
    if (isTitle) {
      setTitleTokens((prev) => prev.filter((_, i) => i !== index));
    } else {
      setDescTokens((prev) => prev.filter((_, i) => i !== index));
    }
  };

  // ---------------------------
  // Update the editable preview text when tokens or separator changes
  // ---------------------------
  useEffect(() => {
    setTitleTemplateText(
      titleTokens
        .map((token) =>
          token.type === "column" ? `{${token.value}}` : token.value
        )
        .join(titleSeparator)
    );
  }, [titleTokens, titleSeparator]);

  useEffect(() => {
    setDescTemplateText(
      descTokens
        .map((token) =>
          token.type === "column" ? `{${token.value}}` : token.value
        )
        .join(descSeparator)
    );
  }, [descTokens, descSeparator]);

  // ---------------------------
  // Bulk Action: Create Trello Cards
  // ---------------------------
  // Modify createTrelloCard to accept a memberId and add it to the parameters.
  const createTrelloCard = async (cardData, listId, memberId) => {
    try {
      const url = `https://api.trello.com/1/cards?key=${TRELLO_API_KEY}&token=${TRELLO_API_TOKEN}`;
      const params = new URLSearchParams();
      params.append("idList", listId);
      params.append("name", cardData.title);
      params.append("desc", cardData.description);
      // Append the member id so the card gets assigned
      if (memberId) {
        params.append("idMembers", memberId);
      }
      const response = await fetch(url, {
        method: "POST",
        body: params,
      });
      return await response.json();
    } catch (error) {
      console.error("Error creating Trello card:", error);
      return null;
    }
  };

  // For destination board we assume the first list is used.
  const getFirstListIdForBoard = async (boardId) => {
    try {
      const url = `https://api.trello.com/1/boards/${boardId}/lists?key=${TRELLO_API_KEY}&token=${TRELLO_API_TOKEN}`;
      const response = await fetch(url);
      const lists = await response.json();
      return lists && lists.length > 0 ? lists[0].id : null;
    } catch (error) {
      console.error("Error fetching board lists:", error);
      return null;
    }
  };

  const [createCardsLoading, setCreateCardsLoading] = useState(false);

  const handleCreateCards = async () => {
    if (!selectedBulkBoard) {
      showSnackbar("Please select a Destination Trello Board.", "warning");
      return;
    }
    if (!selectedBulkMember) {
      showSnackbar("Please select a Destination Member.", "warning");
      return;
    }
    if (selectedRowIds.length === 0) {
      showSnackbar("Please select at least one row.", "warning");
      return;
    }

    setCreateCardsLoading(true); // Start loader

    const listId = await getFirstListIdForBoard(selectedBulkBoard);
    if (!listId) {
      showSnackbar(
        "Failed to load destination list for the selected board.",
        "error"
      );
      setCreateCardsLoading(false);
      return;
    }

    // For each selected row, replace any token placeholder like {ColumnName}
    // in the preview text with the actual value from that row.
    const cards = sheetData
      .filter((row) => selectedRowIds.includes(row.id))
      .map((row) => {
        // Replace all instances of {columnName} with the actual row value.
        const title = titleTemplateText.replace(/{(.*?)}/g, (_, colName) => {
          return row[colName] || "";
        });
        const description = descTemplateText.replace(/{(.*?)}/g, (_, colName) => {
          return row[colName] || "";
        });
        return {
          title,
          description,
        };
      });

    // Create each Trello card with the assigned member.
    for (let card of cards) {
      const result = await createTrelloCard(card, listId, selectedBulkMember);
      console.log("Created card:", result);
    }
    showSnackbar(
      `Created ${cards.length} card(s) on the destination board.`,
      "success"
    );
    setCreateCardsLoading(false); // End loader
  };

  // ---------------------------
  // Pagination Handlers for Upload Table
  // ---------------------------
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Compute unique values for a given column (for filter options)
  const getUniqueValuesForColumn = (col) => {
    const values = new Set(sheetData.map((row) => row[col]).filter(Boolean));
    return Array.from(values);
  };

  // ---------------------------
  // Load Trello Boards & Members for Bulk Action
  // ---------------------------
  const loadBulkBoards = useCallback(async () => {
    try {
      const url = `https://api.trello.com/1/members/me/boards?key=${TRELLO_API_KEY}&token=${TRELLO_API_TOKEN}`;
      const response = await fetch(url);
      const boards = await response.json();
      setBulkBoards(boards);
    } catch (error) {
      console.error("Error loading bulk boards:", error);
    }
  }, []);

  const loadBulkMembers = useCallback(async (boardId) => {
    try {
      const url = `https://api.trello.com/1/boards/${boardId}/members?key=${TRELLO_API_KEY}&token=${TRELLO_API_TOKEN}`;
      const response = await fetch(url);
      const members = await response.json();
      setBulkMembers(members);
    } catch (error) {
      console.error("Error loading bulk board members:", error);
    }
  }, []);

  // Load boards on mount (for bulk action)
  useEffect(() => {
    loadBulkBoards();
  }, [loadBulkBoards]);

  // When selectedBulkBoard changes, load its members
  useEffect(() => {
    if (selectedBulkBoard) {
      loadBulkMembers(selectedBulkBoard);
    } else {
      setBulkMembers([]);
    }
  }, [selectedBulkBoard, loadBulkMembers]);

  return (
    <Box sx={{ p: 3, maxWidth: "1200px", margin: "auto" }}>
      <Typography variant="h5" gutterBottom>
        Upload Your Google Worksheet (CSV)
      </Typography>

      {/* --- Drag & Drop Area --- */}
      <Paper
        elevation={isDragging ? 6 : 2}
        sx={{
          border: "2px dashed #ccc",
          borderRadius: 2,
          p: 4,
          mb: 2,
          textAlign: "center",
          backgroundColor: isDragging ? "#f0f8ff" : "transparent",
          transition: "all 0.2s ease-in-out",
        }}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Typography variant="body1" gutterBottom>
          Drag & drop your CSV file here
        </Typography>
        <Typography variant="body2">or</Typography>
        <Button variant="contained" component="label" sx={{ mt: 1 }}>
          Browse Files
          <input type="file" accept=".csv" hidden onChange={handleFileUpload} />
        </Button>
      </Paper>

      {sheetData.length > 0 && (
        <>
          {/* =========================
              Template Builder: Title Template
          ========================= */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="h6">Title Template</Typography>
              <Typography variant="caption">
                Cards Selected: {selectedRowIds.length}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel id="title-column-select-label">
                  Add Column
                </InputLabel>
                <Select
                  labelId="title-column-select-label"
                  value={selectedColumnForTitle}
                  onChange={(e) => setSelectedColumnForTitle(e.target.value)}
                  label="Add Column"
                >
                  {availableColumns.map((col) => (
                    <MenuItem key={col} value={col}>
                      {col}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="outlined" onClick={addColumnToTitle}>
                Add Column
              </Button>
              <TextField
                label="Static Text"
                size="small"
                value={newTitleStatic}
                onChange={(e) => setNewTitleStatic(e.target.value)}
              />
              <Button variant="outlined" onClick={addStaticToTitle}>
                Add Static
              </Button>
            </Box>
            <FormControl sx={{ minWidth: 200, mb: 2 }} size="small">
              <InputLabel id="title-separator-label">
                Title Separator
              </InputLabel>
              <Select
                labelId="title-separator-label"
                value={titleSeparator}
                onChange={(e) => setTitleSeparator(e.target.value)}
                label="Title Separator"
              >
                {separatorOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
              {titleTokens.map((token, index) => (
                <Paper
                  key={index}
                  sx={{
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography variant="body2">
                    {token.type === "column"
                      ? `[${token.value}]`
                      : token.value}
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <IconButton
                      size="small"
                      onClick={() => moveUpTitleToken(index)}
                    >
                      ▲
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => moveDownTitleToken(index)}
                    >
                      ▼
                    </IconButton>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleEditToken(token, index, true)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteToken(index, true)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Paper>
              ))}
            </Box>
            {/* Editable preview textarea for Title Template */}
            <TextField
              label="Editable Title Template Preview"
              multiline
              fullWidth
              minRows={2}
              value={titleTemplateText}
              onChange={(e) => setTitleTemplateText(e.target.value)}
              sx={{ mt: 2 }}
            />
          </Paper>

          {/* =========================
              Template Builder: Description Template
          ========================= */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Description Template
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel id="desc-column-select-label">
                  Add Column
                </InputLabel>
                <Select
                  labelId="desc-column-select-label"
                  value={selectedColumnForDesc}
                  onChange={(e) => setSelectedColumnForDesc(e.target.value)}
                  label="Add Column"
                >
                  {availableColumns.map((col) => (
                    <MenuItem key={col} value={col}>
                      {col}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="outlined" onClick={addColumnToDesc}>
                Add Column
              </Button>
              <TextField
                label="Static Text"
                size="small"
                value={newDescStatic}
                onChange={(e) => setNewDescStatic(e.target.value)}
              />
              <Button variant="outlined" onClick={addStaticToDesc}>
                Add Static
              </Button>
            </Box>
            <FormControl sx={{ minWidth: 200, mb: 2 }} size="small">
              <InputLabel id="desc-separator-label">
                Description Separator
              </InputLabel>
              <Select
                labelId="desc-separator-label"
                value={descSeparator}
                onChange={(e) => setDescSeparator(e.target.value)}
                label="Description Separator"
              >
                {separatorOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
              {descTokens.map((token, index) => (
                <Paper
                  key={index}
                  sx={{
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography variant="body2">
                    {token.type === "column"
                      ? `[${token.value}]`
                      : token.value}
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <IconButton
                      size="small"
                      onClick={() => moveUpDescToken(index)}
                    >
                      ▲
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => moveDownDescToken(index)}
                    >
                      ▼
                    </IconButton>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleEditToken(token, index, false)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteToken(index, false)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Paper>
              ))}
            </Box>
            {/* Editable preview textarea for Description Template */}
            <TextField
              label="Editable Description Template Preview"
              multiline
              fullWidth
              minRows={2}
              value={descTemplateText}
              onChange={(e) => setDescTemplateText(e.target.value)}
              sx={{ mt: 2 }}
            />
          </Paper>

          {/* =========================
              Data Table: Uploaded Sheet Data with Dropdown Column Filters,
              "Select Visible" Checkbox, Row Selection and Pagination
          ========================= */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectVisible}
                        onChange={handleSelectVisible}
                      />
                    </TableCell>
                    {availableColumns.map((col) => (
                      <TableCell key={col}>{col}</TableCell>
                    ))}
                  </TableRow>
                  {/* Dropdown Filter Row */}
                  <TableRow>
                    <TableCell />
                    {availableColumns.map((col) => {
                      const options = getUniqueValuesForColumn(col);
                      return (
                        <TableCell key={col}>
                          <FormControl fullWidth size="small">
                            <Select
                              value={columnFilters[col] || "all"}
                              onChange={(e) =>
                                handleColumnFilterChange(col, e.target.value)
                              }
                              displayEmpty
                            >
                              <MenuItem value="all">All</MenuItem>
                              {options.map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                  {opt}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedSheetData.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRowIds.includes(row.id)}
                          onChange={() => handleRowCheckbox(row.id)}
                        />
                      </TableCell>
                      {availableColumns.map((col) => (
                        <TableCell key={col}>{row[col]}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredSheetData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </Paper>

          {/* =========================
              Bulk Action: Create Trello Cards
              (Boards & Members loaded from Trello API)
          ========================= */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel id="dest-board-select-label">
                  Select Destination Board
                </InputLabel>
                <Select
                  labelId="dest-board-select-label"
                  value={selectedBulkBoard}
                  onChange={(e) => {
                    setSelectedBulkBoard(e.target.value);
                    setSelectedBulkMember("");
                  }}
                  label="Select Destination Board"
                >
                  {bulkBoards.map((board) => (
                    <MenuItem key={board.id} value={board.id}>
                      {board.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel id="dest-member-select-label">
                  Select Destination Member
                </InputLabel>
                <Select
                  labelId="dest-member-select-label"
                  value={selectedBulkMember}
                  onChange={(e) => setSelectedBulkMember(e.target.value)}
                  label="Select Destination Member"
                  disabled={!selectedBulkBoard || bulkMembers.length === 0}
                >
                  {bulkMembers.map((member) => (
                    <MenuItem key={member.id} value={member.id}>
                      {member.fullName || member.username || member.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Use the LoadingButton instead of Button */}
              <LoadingButton
                variant="contained"
                onClick={handleCreateCards}
                loading={createCardsLoading}
              >
                Create Trello Cards
              </LoadingButton>
            </Box>
          </Paper>
        </>
      )}

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

/* =========================
   TRELLO SECTION
   (Trello board management with improved UI, bulk actions and move card feature)
========================= */
const TrelloSection = () => {
  // --- States for Source Board, List, Cards, & Filters ---
  const [trelloBoards, setTrelloBoards] = useState([]);
  const [selectedSourceBoard, setSelectedSourceBoard] = useState("");
  const [boardLists, setBoardLists] = useState([]);
  const [selectedSourceList, setSelectedSourceList] = useState("");
  const [cardsData, setCardsData] = useState([]);
  const [selectedCardIds, setSelectedCardIds] = useState([]);

  // Filter and search states
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMember, setFilterMember] = useState("");
  const [sourceBoardMembers, setSourceBoardMembers] = useState([]);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Loading state for progress indicator
  const [loading, setLoading] = useState(false);

  // Bulk action (destination) states for Trello Section (for copying cards)
  const [selectedDestBoard, setSelectedDestBoard] = useState("");
  const [destBoardMembers, setDestBoardMembers] = useState([]);
  const [selectedDestMember, setSelectedDestMember] = useState("");

  // New state for moving cards:
  const [moveTargetBoard, setMoveTargetBoard] = useState("");
  const [moveTargetLists, setMoveTargetLists] = useState([]);
  const [moveTargetList, setMoveTargetList] = useState("");

  // NEW: State and handler for "select visible" cards in this section.
  const [selectVisible, setSelectVisible] = useState(false);
  const handleSelectVisible = (event) => {
    const checked = event.target.checked;
    setSelectVisible(checked);
    const visibleIds = paginatedCards.map((card) => card.id);
    if (checked) {
      setSelectedCardIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    } else {
      setSelectedCardIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    }
  };

  // Snackbar state for success/error messages
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // ---------------------------
  // Trello API Calls for Boards, Lists, Cards, Members
  // ---------------------------
  const loadTrelloBoards = useCallback(async () => {
    setLoading(true);
    try {
      const url = `https://api.trello.com/1/members/me/boards?key=${TRELLO_API_KEY}&token=${TRELLO_API_TOKEN}`;
      const response = await fetch(url);
      const boards = await response.json();
      setTrelloBoards(boards);
    } catch (error) {
      console.error("Error loading Trello boards:", error);
      showSnackbar("Error loading Trello boards.", "error");
    }
    setLoading(false);
  }, []);

  const loadBoardLists = useCallback(async (boardId) => {
    setLoading(true);
    try {
      const url = `https://api.trello.com/1/boards/${boardId}/lists?key=${TRELLO_API_KEY}&token=${TRELLO_API_TOKEN}`;
      const response = await fetch(url);
      const lists = await response.json();
      setBoardLists(lists);
    } catch (error) {
      console.error("Error loading board lists:", error);
      showSnackbar("Error loading board lists.", "error");
    }
    setLoading(false);
  }, []);

  const loadCardsForList = useCallback(async (listId) => {
    setLoading(true);
    try {
      const url = `https://api.trello.com/1/lists/${listId}/cards?members=true&key=${TRELLO_API_KEY}&token=${TRELLO_API_TOKEN}`;
      const response = await fetch(url);
      const cards = await response.json();
      setCardsData(cards);
    } catch (error) {
      console.error("Error loading cards for list:", error);
      showSnackbar("Error loading cards for list.", "error");
    }
    setLoading(false);
  }, []);

  const loadBoardMembersSource = useCallback(async (boardId) => {
    setLoading(true);
    try {
      const url = `https://api.trello.com/1/boards/${boardId}/members?key=${TRELLO_API_KEY}&token=${TRELLO_API_TOKEN}`;
      const response = await fetch(url);
      const members = await response.json();
      setSourceBoardMembers(members);
    } catch (error) {
      console.error("Error loading source board members:", error);
      showSnackbar("Error loading source board members.", "error");
    }
    setLoading(false);
  }, []);

  const loadBoardMembersDest = useCallback(async (boardId) => {
    setLoading(true);
    try {
      const url = `https://api.trello.com/1/boards/${boardId}/members?key=${TRELLO_API_KEY}&token=${TRELLO_API_TOKEN}`;
      const response = await fetch(url);
      const members = await response.json();
      setDestBoardMembers(members);
    } catch (error) {
      console.error("Error loading destination board members:", error);
      showSnackbar("Error loading destination board members.", "error");
    }
    setLoading(false);
  }, []);

  // For moving cards: load lists for the selected target board
  const loadMoveTargetLists = useCallback(async (boardId) => {
    try {
      const url = `https://api.trello.com/1/boards/${boardId}/lists?key=${TRELLO_API_KEY}&token=${TRELLO_API_TOKEN}`;
      const response = await fetch(url);
      const lists = await response.json();
      setMoveTargetLists(lists);
    } catch (error) {
      console.error("Error loading move target lists:", error);
      showSnackbar("Error loading target board lists.", "error");
    }
  }, []);

  // ---------------------------
  // useEffect to load Trello boards on mount
  // ---------------------------
  useEffect(() => {
    loadTrelloBoards();
  }, [loadTrelloBoards]);

  // When a source board is selected, load its lists and members
  useEffect(() => {
    if (selectedSourceBoard) {
      loadBoardLists(selectedSourceBoard);
      loadBoardMembersSource(selectedSourceBoard);
      setSelectedSourceList("");
      setCardsData([]);
      setSelectedCardIds([]);
    }
  }, [selectedSourceBoard, loadBoardLists, loadBoardMembersSource]);

  // When a source list is selected, load its cards
  useEffect(() => {
    if (selectedSourceList) {
      loadCardsForList(selectedSourceList);
      setSelectedCardIds([]);
    }
  }, [selectedSourceList, loadCardsForList]);

  // ---------------------------
  // Filtering: Search and Member Filter
  // ---------------------------
  const filteredCards = cardsData.filter((card) => {
    const matchesSearch = card.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesMember =
      filterMember === "" ||
      (card.members &&
        card.members.some((member) => member.id === filterMember));
    return matchesSearch && matchesMember;
  });

  // Pagination: slice the filtered cards
  const paginatedCards = filteredCards.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // ---------------------------
  // Row (Card) Selection Handler
  // ---------------------------
  const handleCardCheckbox = (id) => {
    setSelectedCardIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // ---------------------------
  // Bulk Action: Copy Selected Cards to Destination Board
  // ---------------------------
  const handleCopyCards = async () => {
    if (!selectedDestBoard) {
      showSnackbar("Please select a Destination Trello Board.", "warning");
      return;
    }
    if (!selectedDestMember) {
      showSnackbar("Please select a Destination Member.", "warning");
      return;
    }
    if (selectedCardIds.length === 0) {
      showSnackbar("Please select at least one card.", "warning");
      return;
    }
    setLoading(true);
    const destListId = await (async () => {
      const url = `https://api.trello.com/1/boards/${selectedDestBoard}/lists?key=${TRELLO_API_KEY}&token=${TRELLO_API_TOKEN}`;
      const response = await fetch(url);
      const lists = await response.json();
      return lists.length > 0 ? lists[0].id : null;
    })();
    if (!destListId) {
      showSnackbar("Failed to load destination list.", "error");
      setLoading(false);
      return;
    }
    for (let card of cardsData.filter((c) => selectedCardIds.includes(c.id))) {
      const url = `https://api.trello.com/1/cards?key=${TRELLO_API_KEY}&token=${TRELLO_API_TOKEN}`;
      const params = new URLSearchParams();
      params.append("idList", destListId);
      params.append("name", card.name);
      params.append("desc", card.desc);
      params.append("idMembers", selectedDestMember);
      await fetch(url, { method: "POST", body: params });
    }
    showSnackbar("Cards copied to the destination board.", "success");
    setLoading(false);
  };

  // ---------------------------
  // New Bulk Action: Move Selected Cards to a Specific List in a Target Board
  // ---------------------------
  const handleMoveCards = async () => {
    if (!moveTargetBoard) {
      showSnackbar("Please select a Target Board for moving cards.", "warning");
      return;
    }
    if (!moveTargetList) {
      showSnackbar("Please select a Target List.", "warning");
      return;
    }
    if (selectedCardIds.length === 0) {
      showSnackbar("Please select at least one card to move.", "warning");
      return;
    }
    setLoading(true);
    for (let card of cardsData.filter((c) => selectedCardIds.includes(c.id))) {
      const url = `https://api.trello.com/1/cards/${card.id}?key=${TRELLO_API_KEY}&token=${TRELLO_API_TOKEN}`;
      const params = new URLSearchParams();
      params.append("idList", moveTargetList);
      await fetch(url, { method: "PUT", body: params });
    }
    showSnackbar("Cards moved to the selected list.", "success");
    if (selectedSourceList) {
      loadCardsForList(selectedSourceList);
    }
    setLoading(false);
  };

  // ---------------------------
  // Pagination Handlers for Trello Section
  // ---------------------------
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3, maxWidth: "1200px", margin: "auto" }}>
      <Typography variant="h5" gutterBottom>
        Trello Board Management
      </Typography>
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
          <CircularProgress />
        </Box>
      )}
      {/* --- Source Board Selection --- */}
      <FormControl sx={{ minWidth: 200, mr: 2, mb: 2 }} size="small">
        <InputLabel id="trello-board-select-label">Select Source Board</InputLabel>
        <Select
          labelId="trello-board-select-label"
          value={selectedSourceBoard}
          onChange={(e) => setSelectedSourceBoard(e.target.value)}
          label="Select Source Board"
        >
          {trelloBoards.map((board) => (
            <MenuItem key={board.id} value={board.id}>
              {board.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* --- Source List Selection --- */}
      <FormControl sx={{ minWidth: 200, mb: 2 }} size="small">
        <InputLabel id="trello-list-select-label">Select Source List</InputLabel>
        <Select
          labelId="trello-list-select-label"
          value={selectedSourceList}
          onChange={(e) => setSelectedSourceList(e.target.value)}
          label="Select Source List"
          disabled={!selectedSourceBoard}
        >
          {boardLists.map((list) => (
            <MenuItem key={list.id} value={list.id}>
              {list.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* --- Search and Member Filter --- */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <TextField
          label="Search by Title"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel id="member-filter-label">Filter by Member</InputLabel>
          <Select
            labelId="member-filter-label"
            value={filterMember}
            onChange={(e) => setFilterMember(e.target.value)}
            label="Filter by Member"
          >
            <MenuItem value="">All Members</MenuItem>
            {sourceBoardMembers.map((member) => (
              <MenuItem key={member.id} value={member.id}>
                {member.fullName || member.username || member.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* --- Cards Table --- */}
      <Paper sx={{ mb: 2 }}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectVisible}
                    onChange={handleSelectVisible}
                  />
                </TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Member Name</TableCell>
                <TableCell>Trello Link</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedCards.map((card) => {
                const listObj = boardLists.find((list) => list.id === card.idList);
                const status = listObj ? listObj.name : "Unknown";
                const memberNames =
                  card.members && card.members.length > 0
                    ? card.members
                      .map((m) => m.fullName || m.username || m.name)
                      .join(", ")
                    : "Unassigned";
                return (
                  <TableRow key={card.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedCardIds.includes(card.id)}
                        onChange={() => handleCardCheckbox(card.id)}
                      />
                    </TableCell>
                    <TableCell>{card.name}</TableCell>
                    <TableCell>{status}</TableCell>
                    <TableCell>{memberNames}</TableCell>
                    <TableCell>
                      <a href={card.url} target="_blank" rel="noopener noreferrer">
                        Link
                      </a>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredCards.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      {/* --- Bulk Action: Copy Cards --- */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Bulk Action: Create Trello Cards
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel id="dest-board-select-label">
              Select Destination Board
            </InputLabel>
            <Select
              labelId="dest-board-select-label"
              value={selectedDestBoard}
              onChange={(e) => {
                setSelectedDestBoard(e.target.value);
                loadBoardMembersDest(e.target.value);
                setSelectedDestMember("");
              }}
              label="Select Destination Board"
            >
              {trelloBoards.map((board) => (
                <MenuItem key={board.id} value={board.id}>
                  {board.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel id="dest-member-select-label">
              Select Destination Member
            </InputLabel>
            <Select
              labelId="dest-member-select-label"
              value={selectedDestMember}
              onChange={(e) => setSelectedDestMember(e.target.value)}
              label="Select Destination Member"
              disabled={!selectedDestBoard || destBoardMembers.length === 0}
            >
              {destBoardMembers.map((member) => (
                <MenuItem key={member.id} value={member.id}>
                  {member.fullName || member.username || member.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={handleCopyCards}>
            Create Trello Cards
          </Button>
        </Box>
      </Paper>
      {/* --- Bulk Action: Move Cards to a Specific List --- */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Bulk Action: Move Cards
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel id="move-board-select-label">
              Select Target Board
            </InputLabel>
            <Select
              labelId="move-board-select-label"
              value={moveTargetBoard}
              onChange={(e) => {
                setMoveTargetBoard(e.target.value);
                loadMoveTargetLists(e.target.value);
                setMoveTargetList("");
              }}
              label="Select Target Board"
            >
              {trelloBoards.map((board) => (
                <MenuItem key={board.id} value={board.id}>
                  {board.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            sx={{ minWidth: 200 }}
            size="small"
            disabled={!moveTargetBoard}
          >
            <InputLabel id="move-list-select-label">Select Target List</InputLabel>
            <Select
              labelId="move-list-select-label"
              value={moveTargetList}
              onChange={(e) => setMoveTargetList(e.target.value)}
              label="Select Target List"
            >
              {moveTargetLists.map((list) => (
                <MenuItem key={list.id} value={list.id}>
                  {list.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={handleMoveCards}>
            Move Cards
          </Button>
        </Box>
      </Paper>

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

/* =========================
   BOARDTABLE Component (Tabs for Upload Sheet & Trello)
========================= */
const BoardTable = () => {
  const [activeTab, setActiveTab] = useState("uploadSheet");

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        aria-label="Board Table Tabs"
        sx={{ mb: 2 }}
        centered
      >
        <Tab label="Upload Sheet" value="uploadSheet" />
        <Tab label="Trello" value="trello" />
      </Tabs>
      {activeTab === "uploadSheet" ? (
        <UploadSheetSection />
      ) : (
        <TrelloSection />
      )}
    </Box>
  );
};

export default BoardTable;
